import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { XIcon, WarningIcon, XCircleIcon, InfoLinesIcon } from './icons';

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  onConfirm: () => void;
  onCancel: () => void;
}

const Icons = {
  close: <XIcon className="w-5 h-5" strokeWidth={2} />,
  warning: <WarningIcon className="w-6 h-6" strokeWidth={2} />,
  danger: <XCircleIcon className="w-6 h-6" strokeWidth={2} />,
  info: <InfoLinesIcon className="w-6 h-6" strokeWidth={2} />,
};

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  onConfirm,
  onCancel,
}) => {
  const { isDark } = useTheme();

  if (!isOpen) return null;

  const getIconStyles = () => {
    const styles = {
      danger: isDark ? 'text-red-400 bg-red-500/10' : 'text-red-600 bg-red-50',
      warning: isDark ? 'text-amber-400 bg-amber-500/10' : 'text-amber-600 bg-amber-50',
      info: isDark ? 'text-blue-400 bg-blue-500/10' : 'text-blue-600 bg-blue-50',
    };
    return styles[variant];
  };

  const getConfirmButtonStyles = () => {
    const styles = {
      danger: 'bg-red-600 hover:bg-red-700 text-white',
      warning: 'bg-amber-600 hover:bg-amber-700 text-white',
      info: 'bg-blue-600 hover:bg-blue-700 text-white',
    };
    return styles[variant];
  };

  return (
    <div className="fixed inset-0 z-[10000] overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className={`relative w-full max-w-md transform rounded-2xl p-6 shadow-xl transition-all ${
            isDark
              ? 'bg-slate-900 border border-slate-700'
              : 'bg-white border border-slate-200'
          }`}
        >
          {/* Close Button */}
          <button
            type="button"
            onClick={onCancel}
            className={`absolute top-4 right-4 p-1 rounded-lg transition-colors ${
              isDark
                ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
            }`}
          >
            {Icons.close}
          </button>

          {/* Icon */}
          <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${getIconStyles()}`}>
            {Icons[variant]}
          </div>

          {/* Content */}
          <div className="mt-4 text-center">
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {title}
            </h3>
            <p className={`mt-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {message}
            </p>
          </div>

          {/* Actions */}
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              className={`flex-1 px-4 py-2.5 rounded-xl font-medium transition-colors ${
                isDark
                  ? 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200'
              }`}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className={`flex-1 px-4 py-2.5 rounded-xl font-medium transition-colors ${getConfirmButtonStyles()}`}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
